import { Schema } from "effect"
import { reportRequestFailed } from "./telemetry"

/** JSON over same-origin /api with the session cookie. Responses are decoded with Effect Schema. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The parsed JSON error body, when the server sent one (e.g. `{ error: "not_pending", status: "approved" }`). */
    readonly body: Readonly<Record<string, unknown>> | null = null,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/**
 * The server didn't answer in time (decision 0034): a request we aborted ourselves. An `ApiError` with status 0, so
 * `RouteError` reads it as "Couldn't reach Minga" and the query client never retries it (a retry doubles the wait).
 */
export class RequestTimeout extends ApiError {
  constructor(message = "Minga didn't answer in time.") {
    super(0, message)
    this.name = "RequestTimeout"
  }
}

/** An abort or `AbortSignal.timeout` firing: fetch rejects with a `DOMException` (or an `Error`) of that name. */
export const isAbortOrTimeout = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  (error.name === "TimeoutError" || error.name === "AbortError")

/** "within 10 seconds" for a timeout in millis. */
export const timeoutText = (ms: number): string =>
  `Minga didn't answer within ${Math.round(ms / 1000)} seconds.`

export const isUnauthorized = (error: unknown): boolean => error instanceof ApiError && error.status === 401

/** fetch() rejects with a TypeError when the network or server is unreachable ("Failed to fetch", "Load failed", …). */
export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError && /fetch|network|load failed/i.test(error.message)

export const readError = async (response: Response): Promise<ApiError> => {
  let body: Record<string, unknown> | null = null
  try {
    const json: unknown = await response.json()
    if (json && typeof json === "object" && !Array.isArray(json)) body = json as Record<string, unknown>
  } catch {
    // not JSON
  }
  const message = body?.message ?? body?.error ?? body?.status
  return new ApiError(
    response.status,
    typeof message === "string" ? message : response.statusText || `Request failed (${response.status})`,
    body,
  )
}

/**
 * How long a read (GET) waits by default before it gives up with a `RequestTimeout`: a hung request becomes an error
 * the page shows with a way to try again, not an endless spinner. Writes wait without a limit unless they ask for one
 * (giving up on a write the server may still finish would be misleading).
 */
export const DEFAULT_READ_TIMEOUT_MS = 15_000

export type ApiInit = {
  method?: string
  body?: unknown
  /**
   * Give up after this long (headers and body) with a `RequestTimeout`. Reads default to `DEFAULT_READ_TIMEOUT_MS`;
   * `null` waits without a limit.
   */
  timeoutMs?: number | null
  /**
   * Decision 0160: the fetch to use instead of the global one (the boot's early request made in `index.html`, see
   * `lib/early-boot.ts`). It must honour the request's abort signal.
   */
  fetchImpl?: typeof fetch
}

/** The timeout a request gets: its own, else the read default for a GET, else none. */
export const effectiveTimeout = (init: ApiInit): number | null =>
  init.timeoutMs !== undefined
    ? init.timeoutMs
    : (init.method ?? "GET").toUpperCase() === "GET"
      ? DEFAULT_READ_TIMEOUT_MS
      : null

/**
 * Decision 0143: the request's correlation id (`x-minga-corr`): `cmd:<commandId>` for a command (the id the event log
 * keeps), else a fresh `req:<id>`. The Worker puts it on every log line of the request.
 */
export const correlationFor = (body: unknown): string => {
  const commandId = (body as { commandId?: unknown } | null | undefined)?.commandId
  if (typeof commandId === "string" && /^[A-Za-z0-9_.-]{1,110}$/.test(commandId)) return `cmd:${commandId}`
  const id = globalThis.crypto?.randomUUID?.().replaceAll("-", "").slice(0, 20) ?? String(Date.now())
  return `req:${id}`
}

export async function apiRequest<S extends Schema.ConstraintDecoder<unknown>>(
  path: string,
  schema: S | null,
  init: ApiInit = {},
): Promise<S["Type"] | null> {
  const correlationId = correlationFor(init.body)
  const headers: Record<string, string> = { accept: "application/json", "x-minga-corr": correlationId }
  const request: RequestInit = { method: init.method ?? "GET", credentials: "same-origin", headers }
  if (init.body !== undefined) {
    headers["content-type"] = "application/json"
    request.body = JSON.stringify(init.body)
  }
  const timeoutMs = effectiveTimeout(init)
  if (timeoutMs !== null) request.signal = AbortSignal.timeout(timeoutMs)
  const startedAt = Date.now()
  const failed = (status: number, errorCode?: unknown) =>
    reportRequestFailed({
      path,
      method: request.method ?? "GET",
      status,
      ms: Date.now() - startedAt,
      correlationId,
      ...(typeof errorCode === "string" && /^[a-z0-9_]{1,64}$/.test(errorCode) ? { errorCode } : {}),
    })
  try {
    const response = await (init.fetchImpl ?? fetch)(path, request)
    if (!response.ok) {
      const error = await readError(response)
      failed(response.status, error.body?.error)
      throw error
    }
    if (!schema || response.status === 204) return null
    const json: unknown = await response.json()
    return Schema.decodeUnknownSync(schema)(json)
  } catch (error) {
    if (timeoutMs !== null && isAbortOrTimeout(error)) {
      failed(0, "timeout")
      throw new RequestTimeout(timeoutText(timeoutMs))
    }
    if (isNetworkError(error)) failed(0, "network")
    throw error
  }
}

export async function apiJson<S extends Schema.ConstraintDecoder<unknown>>(
  path: string,
  schema: S,
  init: ApiInit = {},
): Promise<S["Type"]> {
  const value = await apiRequest(path, schema, init)
  if (value === null) throw new ApiError(204, "Empty response")
  return value
}

/** Accepts epoch millis or an ISO string; the runner contract does not pin the encoding yet. */
export const Instant = Schema.Union([Schema.Number, Schema.String])
export const toDate = (value: number | string | null | undefined): Date | null => {
  if (value === null || value === undefined) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}
